import { Injectable, OnDestroy } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { pluck } from 'rxjs/operators'
import {
  AlgorithmChoice,
  LoadStatus,
  MachineState,
  OperatorChoice,
  State,
  TagRecord,
} from '../models/states.models'
import {
  ComputedData,
  ComputedPairingStatus,
  ComputedRecord,
  ComputedState,
  ComputedStatus,
  MachineStateComputed,
} from '../models/states-computed.models'
import { TouaregControlStateActionsService } from './touareg-control-state-actions.service'
//import { PairingService } from '@/service/pairing/pairing.service'
import { TouaregControlStateParamsService } from './touareg-control-state-params.service'

interface DataListCache {
  computed: ComputedData
  cache: Record<string | number, UIControllerList>
}

export enum UIControllerList {
  selection = 'selection',
  suggestion = 'suggestion',
}

@Injectable({
  providedIn: 'root',
})
export class TouaregControlStateComputeService implements OnDestroy {
  private _previousState: MachineStateComputed | null = null
  private _previousStateCacheList: Record<string | number, UIControllerList> = {}
  private _premiumPairingField: string | undefined
  //private _pairingFieldsSubs: Subscription

  constructor(
    private _actionService: TouaregControlStateActionsService,
    private _paramsService: TouaregControlStateParamsService,
    private _translate: TranslateService //private _pairings: PairingService
  ) {
    // On s'abonne au pairing fields
    // Et on prend le premier de la liste
    // Que l'on élit "Premium"
    // this._pairingFieldsSubs = this._pairings.pairingFields$
    //   .pipe(pluck('0'))
    //   .subscribe((field) => (this._premiumPairingField = (field as any)?.name))
  }

  ngOnDestroy() {
    //if (!!this._pairingFieldsSubs) this._pairingFieldsSubs.unsubscribe()
  }

  private _localAddToList(
    tags: TagRecord[],
    destination: UIControllerList
  ): DataListCache {
    const selection = [...this._previousState!.computed.selection]
    const suggestion = [...this._previousState!.computed.suggestion]
    const cache: Record<string | number, UIControllerList> = {}

    // Si on bascule les tag vers la sélection
    if (destination === UIControllerList.selection) {
      for (let record of tags) {
        cache[record.tag_uid] = UIControllerList.selection
        selection.push({ record, computedState: ComputedState.loading })
      }
    } // Si on basculte vers la suggestion
    else if (destination === UIControllerList.suggestion) {
      for (let record of tags) {
        cache[record.tag_uid] = UIControllerList.suggestion
        suggestion.push({ record, computedState: ComputedState.loading })
      }
    } else {
      console.error(`Wrong destination list "${destination}""`)
    }

    return { computed: { suggestion, selection }, cache }
  }

  private _localSwitchToList(
    taguids: (string | number)[],
    destination: UIControllerList
  ): DataListCache {
    let suggestion: ComputedRecord[] = []
    let selection: ComputedRecord[] = []
    const cache: Record<string | number, UIControllerList> = {}

    // Si on bascule les tag vers la sélection
    if (destination === UIControllerList.selection) {
      // Tous les tags actuellement en selection restent dans la même liste
      selection = [...this._previousState!.computed.selection]
      // Pour les tags en suggestion, on traque ceux qui doivent basculer en selection
      for (let data of this._previousState!.computed.suggestion) {
        const record = data.record
        if (taguids.indexOf(record.tag_uid) !== -1) {
          // Ceux qui basculent sont en état de load
          cache[record.tag_uid] = UIControllerList.selection
          selection.push({ record, computedState: ComputedState.loading })
        } else {
          // Ceux qui restent gardent leur état courant
          cache[record.tag_uid] = UIControllerList.suggestion
          suggestion.push({
            record,
            computedState: data.computedState,
            computedStatus: data?.computedStatus,
            computedStatusMessage: data?.computedStatusMessage,
          })
        }
      }
    }
    // Si on basculte vers la suggestion
    else if (destination === UIControllerList.suggestion) {
      // Tous les tags actuellement en suggestion restent dans la même liste
      suggestion = [...this._previousState!.computed.suggestion]
      // Pour les tags en selection, on traque ceux qui doivent basculer en suggestion
      for (let data of this._previousState!.computed.selection) {
        const record = data.record
        if (taguids.indexOf(record.tag_uid) !== -1) {
          // Ceux qui basculent sont en état de load
          cache[record.tag_uid] = UIControllerList.suggestion
          suggestion.push({ record, computedState: ComputedState.loading })
        } else {
          // Ceux qui restent gardent leur état courant
          cache[record.tag_uid] = UIControllerList.selection
          selection.push({
            record,
            computedState: data.computedState,
            computedStatus: data?.computedStatus,
            computedStatusMessage: data?.computedStatusMessage,
          })
        }
      }
    } else {
      console.error(`Wrong destination list "${destination}""`)
    }

    return { computed: { suggestion, selection }, cache }
  }

  private _parseListWithoutPreviousState(state: MachineState): DataListCache {
    const { tags_list } = state
    const suggestion: ComputedRecord[] = []
    const selection: ComputedRecord[] = []
    const cache: Record<string | number, UIControllerList> = {}

    if (tags_list) {
      for (let record of tags_list) {
        if (record.load_status === LoadStatus.loaded) {
          cache[record.tag_uid] = UIControllerList.selection
          selection.push({ record, computedState: ComputedState.stable })
        } else {
          cache[record.tag_uid] = UIControllerList.suggestion
          suggestion.push({ record, computedState: ComputedState.stable })
        }
      }
    }

    return { computed: { suggestion, selection }, cache }
  }

  private _parseListWithPreviousState(
    state: MachineState,
    previousState: MachineStateComputed,
    previousStateCache: Record<string | number, UIControllerList>
  ): DataListCache {
    const suggestion: ComputedRecord[] = []
    const selection: ComputedRecord[] = []
    const cache: Record<string | number, UIControllerList> = {}

    // On va stocker les tag déjà calculés au fur et à mesure
    const already: Record<string | number, boolean> = {}

    // On  check si un previous state a déjà été calculé
    // Si ce n'est pas le cas, alors il s'agit d'un premier calcul
    // Et les listes UI sont vides.
    if (!previousStateCache || !previousState) {
      return this._parseListWithoutPreviousState(state)
    }

    // Sinon, on extrait les listes déjà calculés précédemment
    const previousSuggestion = previousState.computed.suggestion
    const previousSelection = previousState.computed.selection

    const { tags_list } = state

    // ETAPE 1 : Traitement de tous les tags renvoyés par la machine
    // On boucle sur la
    if (tags_list) {
      for (let record of tags_list) {
        // Pour chaque tag, on flag le already
        already[record.tag_uid] = true
        // Puis on détermine dans quelle liste il doit se retrouver,
        // selon qu'il est en état de loading ou pas
        if (this._actionService.isPending(record.tag_uid)) {
          // Si en état de load, alors laisse la tag dans la liste de l'état précédent
          if (
            previousStateCache[record.tag_uid] === UIControllerList.selection
          ) {
            cache[record.tag_uid] = UIControllerList.selection
            selection.push({ record, computedState: ComputedState.loading })
          } else {
            cache[record.tag_uid] = UIControllerList.suggestion
            suggestion.push({ record, computedState: ComputedState.loading })
          }
        } else {
          // console.log('NO LOADING', record)
          // S'il n'est pas en état de load
          // On détermine sa liste de destination à partir de son status machine
          if (record.load_status === LoadStatus.loaded) {
            cache[record.tag_uid] = UIControllerList.selection
            selection.push({ record, computedState: ComputedState.stable })
          } else {
            cache[record.tag_uid] = UIControllerList.suggestion
            suggestion.push({ record, computedState: ComputedState.stable })
          }
        }
      }
    }

    // On poursuit par les tags en attente
    // Dans la précédente liste de suggestion
    for (let ps of previousSuggestion) {
      const record = ps.record
      // si le tag est deja traité, on ne s'en occupe plus
      if (already[record.tag_uid]) continue
      // Si le tag est en etat loading seulement,
      // On l'ajoute à la liste finale de suggestion
      if (this._actionService.isPending(record.tag_uid)) {
        cache[record.tag_uid] = UIControllerList.suggestion
        suggestion.push({ record, computedState: ComputedState.loading })
      }
    }

    // On poursuit par les tags en attente
    // Dans la précédente liste de selection
    for (let ps of previousSelection) {
      const record = ps.record
      // si le tag est deja traité, on ne s'en occupe plus
      if (already[ps.record.tag_uid]) continue
      // Si le tag est en etat loading seulement,
      // On l'ajoute à la liste finale de sélection
      if (this._actionService.isPending(record.tag_uid)) {
        cache[record.tag_uid] = UIControllerList.selection
        selection.push({ record, computedState: ComputedState.loading })
      }
    }

    return { computed: { suggestion, selection }, cache }
  }

  /**
   *
   * @param state
   */
  public computeStateFromMachine(state: MachineState): MachineStateComputed | null {
    // Si pas d'état fourni, on stoppe ici et on nettoie.
    if (!state) {
      this.clear()
      return null
    }

    // // Si l'on change de session
    // // On reset le tout
    // if(this._hasSessionChange(state)) {
    //   this._actionService.clear()
    //   this.clear()
    //   return null
    // }

    // Si pas d'état précédent, alors on trie simplement les tags
    // sans tenir compte d'une état loading ou pas
    // Le computedState de chaque record est à "stable"
    if (!this._previousState) {
      const { computed, cache } = this._parseListWithoutPreviousState(state)
      this._previousState = { ...state, computed }
      this._previousStateCacheList = cache
    } else {
      // Si on a un état précédent, on doit vérifier
      const { computed, cache } = this._parseListWithPreviousState(
        state,
        this._previousState,
        this._previousStateCacheList
      )
      this._previousState = { ...state, computed }
      this._previousStateCacheList = cache
    }

    // On refait un tour pour lever des warning ou des conflits
    // sur chaque computedRecord
    this._previousState = this._checkWarningsAndConflicts(this._previousState)

    // Retour de l'état fraîchement calculé
    return this._previousState
  }

  /**
   * Check et le cas échéant lève un status de conflit ou de warning
   * pour chaque conputedRecord.
   * La fonction agit comme un mutateur : elle renvoie le même state avec les mêmes record
   *
   * @param state
   * @returns
   */
  private _checkWarningsAndConflicts(
    state: MachineStateComputed
  ): MachineStateComputed {
    // On récupère les settings d'affichage des alertes
    const { dropAlertAlgo } = this._paramsService.settings || {}

    // Si on est dans un drop acception user choice state en mode manuel,
    // alors la UI a besoin d'afficher d'eventuels états de conflits
    // ou des warning, si des divergences existent entre les choix de l'algo et du user
    if (
      dropAlertAlgo &&
      state?.computed &&
      (state.state === State.DROP_ACCEPTING_USER_CHOICE ||
        state.state === State.DROP_BLOCKED_BY_USER)
    ) {
      const { suggestion, selection } = state.computed
      const warningList: string[] = []

      suggestion.forEach((row) => {
        if (row.computedState !== ComputedState.loading) {
          const conflict = this._checkConflict(warningList, row)
          if (!conflict) this._checkWarning(warningList, row)
        }
      })

      selection.forEach((row) => {
        if (row.computedState !== ComputedState.loading) {
          const conflict = this._checkConflict(warningList, row)
          if (!conflict) this._checkWarning(warningList, row)
        }
      })

      state.computed.warningList = warningList
    }

    return state
  }

  /**
   * Regarde la premiere pairing value de chaque tag, et aggrès les valeurs dans un array
   * Si elles ne sont pas identiques pour chaque tag
   * Met les tags concernés conflit
   * Fonction qui agit comme une mutateur sur l'objet
   *
   * @param selection
   * @returns
   */
  // private _checkPairingConflict(
  //   selection: ComputedRecord[],
  //   pairingWarningList: string[]
  // ) {
  //   const cacheValues = {}
  //   const firstPairingValuesList = []

  //   // Si on bien reçu le pairing field à check
  //   if (this._premiumPairingField) {
  //     selection.forEach((row) => {
  //       if (row.computedState !== ComputedState.loading) {
  //         const facets = row.record?.gem?.facets
  //         if (facets && facets[this._premiumPairingField]) {
  //           const value = facets[this._premiumPairingField]?.content
  //           if (value) {
  //             // Si la valeur n'est pas deja enregistrée
  //             if (!cacheValues[value]) {
  //               // On lève un conflit si une valeur différente pré-existe dans la liste
  //               if (firstPairingValuesList.length) {
  //                 // On lève le flag conflit
  //                 cacheValues[value] = 'conflict'
  //                 row.computedPairingStatus = ComputedPairingStatus.conflict
  //                 row.computedPairingStatusMessage = this._translate.instant('touaregController.pairingFieldDiffers', { field: this._premiumPairingField })
  //                 pairingWarningList.push(
  //                   `Tag ${row.record.tag_uid} - ${row.computedPairingStatusMessage}`
  //                 )
  //               } else {
  //                 // On lève le flag
  //                 cacheValues[value] = true
  //               }
  //               // On ajoute la valeur à la liste
  //               firstPairingValuesList.push(value)
  //             }
  //             // Si la valeur est déjà enregistré comme un conflit
  //             else if (cacheValues[value] === 'conflict') {
  //               row.computedPairingStatus = ComputedPairingStatus.conflict
  //               row.computedPairingStatusMessage = this._translate.instant('touaregController.pairingFieldDiffers', { field: this._premiumPairingField })
  //               pairingWarningList.push(
  //                 `Tag ${row.record.tag_uid} - ${row.computedPairingStatusMessage}`
  //               )
  //             }
  //           } else {
  //             // No pairing field value
  //             row.computedPairingStatus = ComputedPairingStatus.conflict
  //             row.computedPairingStatusMessage = this._translate.instant('touaregController.pairingFieldEmpty', { field: this._premiumPairingField })
  //             pairingWarningList.push(
  //               `Tag ${row.record.tag_uid} - ${row.computedPairingStatusMessage}`
  //             )
  //             firstPairingValuesList.push('???')
  //           }
  //         } else {
  //           // No pairing field
  //           row.computedPairingStatus = ComputedPairingStatus.conflict
  //           row.computedPairingStatusMessage = this._translate.instant('touaregController.pairingFieldEmpty', { field: this._premiumPairingField })
  //           pairingWarningList.push(
  //             `Tag ${row.record.tag_uid} - ${row.computedPairingStatusMessage}`
  //           )
  //           firstPairingValuesList.push('???')
  //         }
  //       }
  //     })
  //   }

  //   return {
  //     firstPairingValuesList,
  //     firstPairingName: this._premiumPairingField,
  //   }
  // }

  /**
   * Met un record en état de conflit si besoin, et renvoie un boolean à true
   * si un conflit est détecté
   * Fonction qui agit comme une mutateur sur l'objet
   *
   * @param warningList
   * @param computedRecord
   * @returns
   */
  private _checkConflict(
    warningList: string[],
    computedRecord: ComputedRecord
  ): boolean {
    const uid = computedRecord.record.tag_uid
    const ac = computedRecord.record.algo_choice
    const oc = computedRecord.record.operator_choice

    // Si l'une des parties n'a pas choisi, alors pas de conflit
    if (ac === AlgorithmChoice.no_choice || oc === OperatorChoice.no_choice) {
      return false
    }

    // Si les deux parties ont des opinions différentes
    // Alors il s'agit d'un conflit
    if (
      ac === AlgorithmChoice.loaded_confirmed &&
      oc !== OperatorChoice.loaded_confirmed
    ) {
      computedRecord.computedStatus = ComputedStatus.conflict
      computedRecord.computedStatusMessage = this._translate.instant('touaregController.systemDetectedAsset')
      warningList.push(
        this._translate.instant('touaregController.conflictRejectedTag', { uid })
      )
      return true
    }

    // Si les deux parties ont des opinions différentes
    // Alors il s'agit d'un conflit
    if (
      ac === AlgorithmChoice.loaded_rejected &&
      oc !== OperatorChoice.loaded_rejected
    ) {
      computedRecord.computedStatus = ComputedStatus.conflict
      computedRecord.computedStatusMessage = this._translate.instant('touaregController.systemHasNotSeenAsset')
      warningList.push(
        this._translate.instant('touaregController.conflictSelectedTag', { uid })
      )
      return true
    }

    return false
  }

  /**
   * Met un record en état de warning si besoin, et renvoie un boolean à true
   * si un warning est détecté
   * Fonction qui agit comme une mutateur sur l'objet
   * @param state
   * @param computedRecord
   * @returns
   */
  private _checkWarning(
    warningList: string[],
    computedRecord: ComputedRecord
  ): boolean {
    // @TODO ==== A REMPLACER PAR UNE CONFIG en BACK !
    const AUTO_ADD_MISSING_ON = true

    const uid = computedRecord.record.tag_uid
    const ac = computedRecord.record.algo_choice
    const ls = computedRecord.record.load_status

    if (AUTO_ADD_MISSING_ON) {
      return false
    }

    if (
      ac === AlgorithmChoice.loaded_confirmed &&
      ls === LoadStatus.not_loaded
    ) {
      computedRecord.computedStatus = ComputedStatus.warning
      computedRecord.computedStatusMessage = this._translate.instant('touaregController.systemClearlySawAsset')
      warningList.push(
        this._translate.instant('touaregController.warningTagNotSelected', { uid })
      )
      return true
    }

    return false
  }

  /**
   *
   * @param tags
   * @param destination
   * @returns
   */
  public localSwitchAndCompute(
    tags: TagRecord[],
    destination: UIControllerList
  ): MachineStateComputed | null | undefined {
    if (!this._previousState?.computed) {
      console.log(
        'There is no previous state list. Cannont switch to destinaton list.'
      )
      return
    }
    const taguids = tags.map((t) => t.tag_uid)
    const { computed, cache } = this._localSwitchToList(taguids, destination)
    this._previousState = { ...this._previousState, computed }
    this._previousStateCacheList = { ...this._previousStateCacheList, ...cache }

    return this._previousState
  }

  /**
   *
   * @param tags
   * @param destination
   * @returns
   */
  public localAddAndCompute(
    tags: TagRecord[],
    destination: UIControllerList
  ): MachineStateComputed | null | undefined {
    if (!this._previousState?.computed) {
      console.log(
        'There is no previous state list. Cannont add to destinaton list.'
      )
      return
    }
    const { computed, cache } = this._localAddToList(tags, destination)
    this._previousState = { ...this._previousState, computed }
    this._previousStateCacheList = { ...this._previousStateCacheList, ...cache }

    return this._previousState
  }

  public clear() {
    this._previousState = null
    this._previousStateCacheList = {}
  }

  public reCompute(state?: MachineState): MachineStateComputed | null {
    const previousState = state ? state : this._previousState
    if (!previousState) {
      console.log(
        'Re compute : No previous state found. Abord computing state from last remote state machine.'
      )
      return null
    }
    this.computeStateFromMachine(previousState)
    return this._previousState
  }

  public restore(state?: MachineState): MachineStateComputed | null {
    const previousState = state ? state : this._previousState
    this._actionService.clear()
    this.clear()
    if (!previousState) {
      console.log(
        'Restore : No previous state found. Abord computing state from remote state machine.'
      )
      return null
    }
    this.computeStateFromMachine(previousState)
    return this._previousState
  }
}
